import Chartist from 'ascom-chartist-logscale';
import mergeWith from 'lodash-es/mergeWith';
import tooltip from 'ascom-chartist-tooltip'; // TODO Invalid addEventListener call needs to be fixed inside plugin-tooltips (https://jira.ascom-ws.com/browse/MERA-1544)
import legend from 'ascom-chartist-legend'; // TODO Type error needs to be fixed inside plugin-legend (https://jira.ascom-ws.com/browse/MERA-1545)
import reverse from 'lodash-es/reverse';
import { i18n } from '../../../localization';
import { getCancelToken } from '../../../api';
import { secsToReadableTime } from '../../common/helpers';

export const horizontalBarBuilder = (data, ref, className, callTypes, customTooltip) => {
    let callTypeIds = Object.keys(data[Object.keys(data)[0]]).map(Number);
    let callTypeIdsOrdered = callTypes.map(el => el.id).filter(el => callTypeIds.includes(el));

    let labels = reverse(getChartLabels(data));
    let seriesDisordered = getChartSeries(data, labels, callTypes);
    let series = reverse(
        callTypeIdsOrdered.map(callTypeId => {
            let callType = callTypes.find(t => t.id === callTypeId).name;
            return seriesDisordered.find(s => s.meta === callType);
        })
    );

    let chartData = { labels, series };
    let options = horizontalBarConfiguration(labels, ref, callTypeIdsOrdered, callTypes, customTooltip);

    new Chartist.Bar(className, chartData, options);
};

/**
 * List of labels for chart
 * @param {object} data
 * @returns {string[]}
 */
const getChartLabels = data => Object.keys(data);

const customizer = (c, v) => (c === undefined ? [v] : c.concat([v]));

/**
 * Merging data between associative arrays, then mapping meta and classNames to it
 * @param {array} data
 * @param {array} labels
 * @param {object} callTypes
 * @returns {{value: *, meta, className: string}[]}
 */
const getChartSeries = (data, labels, callTypes) => {
    let arr = [];

    for (let name of labels) {
        mergeWith(arr, data[name], customizer);
    }

    return arr
        .map((value, key) => ({
            value,
            meta: callTypes.find(el => el.id === key).name,
            className: getClassNameById(key),
        }))
        .filter(val => val);
};

/**
 * Merge data between objects for Week response time performance chart
 * @param {Array} data
 * @param {Array} meta
 * @param {Boolean} moreThan26Weeks
 * @returns {*[]}
 */
export const verticalChartSeries = (data, meta, moreThan26Weeks) => {
    let assoc = [];

    for (let el of data) {
        mergeWith(assoc, el, customizer);
    }

    if (moreThan26Weeks) {
        assoc.within_target = assoc.within_target.slice(-26);
        assoc.delayed = assoc.delayed.slice(-26);
        assoc.late = assoc.late.slice(-26);
    }

    return [
        { meta: meta[0], value: assoc.within_target },
        { meta: meta[1], value: assoc.delayed },
        { meta: meta[2], value: assoc.late },
    ];
};

const horizontalBarConfiguration = (labels, ref, callTypeIds, callTypes, customTooltip) => {
    let labelsLength = labels.length;
    let linesCount = Math.max(callTypeIds.length, 3);
    let linesHeight = (labelsLength > 10 ? 15 : labelsLength > 5 ? 20 : 25) * labelsLength;
    let height = linesCount * linesHeight + 50 + 'px';
    ref.innerHTML = '';

    return {
        horizontalBars: true,
        axisX: {
            scale: 'log10',
        },
        axisY: {
            offset: 120,
        },
        chartPadding: {
            right: 40,
        },
        seriesBarDistance: labelsLength > 10 ? 12 : labelsLength > 5 ? 15 : 20,
        height,
        plugins: [
            legend({
                legendNames: callTypeIds.map(id => callTypes.find(el => el.id === Number(id)).name),
                position: ref,
                clickable: false,
                classNames: callTypeIds.map(id => getClassNameById(Number(id))),
            }),
            tooltip({
                tooltipFnc: (name, val) => {
                    if (customTooltip) {
                        return name + ': ' + secsToReadableTime(Number(val));
                    }
                    return name + ': ' + val;
                },
            }),
        ],
    };
};

/**
 * Configuration for Week response time performance chart
 * @param {Number} w
 * @param {Array} legendNames
 * @returns {{fullWidth: boolean, seriesBarDistance: *, height: string, axisY: {onlyInteger: boolean, high: number, low: number, ticks: number[]}, plugins: *[]}}
 */
export const exceptionChartConfiguration = (w, legendNames) => {
    return {
        fullWidth: true,
        seriesBarDistance: w,
        height: '300px',
        axisY: {
            onlyInteger: true,
            high: 100,
            low: 0,
            ticks: [25, 50, 70],
        },
        plugins: [
            legend({
                legendNames,
                className: 'exception-report-legend',
                clickable: false,
            }),
            tooltip({
                tooltipFnc: (name, val) => {
                    return name + ': ' + val + '%';
                },
            }),
        ],
    };
};

export const fullEventChartConfiguration = series => {
    let totalCallsDuration = series.reduce((acc, el) => acc + el.value, 0);

    return {
        donut: true,
        showLabel: false,
        donutWidth: 45,
        plugins: [
            legend({
                legendNames: series.map(serie => `${serie.meta}: ${secsToReadableTime(serie.value)}`),
                position: 'top',
                clickable: false,
                classNames: series.map(serie => serie.className),
            }),
            tooltip({
                tooltipFnc: (name, val) => {
                    return name + ': ' + Math.round((val / totalCallsDuration) * 100) + '%';
                },
            }),
        ],
    };
};

/**
 * Define class for call type category
 * @param {Number} categoryId
 * @return {String}
 */
const getClassNameById = categoryId => {
    switch (categoryId) {
        case 1:
            return 'ct-normal-bar';
        case 2:
            return 'ct-assistance-bar';
        case 3:
            return 'ct-emergency-bar';
        case 10:
            return 'ct-medical-high-bar';
        case 4:
            return 'ct-medical-medium-bar';
        case 9:
            return 'ct-medical-low-bar';
        case 5:
            return 'ct-error-bar';
        case 6:
            return 'ct-presence-bar';
        case 7:
            return 'ct-technical-bar';
        case 8:
            return 'ct-security-bar';
        case 11:
            return 'ct-service-bar';
        default:
            return '';
    }
};

/**
 * Returns true if colored icon should be rendered
 * @param {string} event
 * @returns {boolean}
 */
export const showColoredIcon = event => {
    let i = event.split('.')[0];
    return (
        i === 'message' ||
        i === 'accept' ||
        i === 'reject' ||
        i === 'remote_cancel' ||
        i === 'speech' ||
        i === 'speech_disconnect' ||
        i === 'user_in' ||
        i === 'user_out'
    );
};

/**
 * Returns name of svg icon or class name for icon in font
 * @param {string} event
 * @returns {string}
 */
export const iconClass = event => {
    return event.split('.')[0];
};

/**
 * Go to the selected page. If the transition to this page has already been implemented, then it will be
 * substituted from the cache. Otherwise the page will be loaded with ajax.
 */
export function flipThePage() {
    if (this.cancelPaginationToken) {
        this.cancelPaginationToken.cancel();
    }

    this.cancelPaginationToken = getCancelToken();

    if (this.cache.hasOwnProperty(this.pagination.current_page)) {
        this.reportData = this.cache[this.pagination.current_page];
    } else {
        this.loadTableData();
    }
}

export function toCachePages(pages) {
    Object.keys(this.cache).forEach(page => {
        if (!pages.includes(parseInt(page))) {
            delete this.cache[page];
        }
    });
}

/**
 * Is call trace incomplete or without end time?
 * @param call must have incomplete and call_end or end_time properties
 * @returns {boolean}
 */
export const isUnfinished = call => {
    return (
        call.incomplete ||
        (call.hasOwnProperty('call_end') && !call.call_end) ||
        (call.hasOwnProperty('end_time') && !call.end_time)
    );
};

/**
 * Returns state of unfinished call trace
 * @param call must have incomplete and call_end or end_time properties
 * @returns {string}
 */
export const getUnfinishedText = call => {
    if (call.incomplete) {
        return i18n.t('reports.unknown');
    } else if (
        (call.hasOwnProperty('call_end') && !call.call_end) ||
        (call.hasOwnProperty('end_time') && !call.end_time)
    ) {
        return i18n.t('reports.ongoing');
    }

    return '-';
};
